import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useProductCategories } from '@/hooks/warehouse/useProductCategories';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';
import { useEffect } from 'react';

const formSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
});

type FormData = z.infer<typeof formSchema>;

interface ProductCategoryFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryId?: string | null;
  initialName?: string;
  onSuccess?: () => void;
}

export default function ProductCategoryFormModal({
  open,
  onOpenChange,
  categoryId,
  initialName,
  onSuccess
}: ProductCategoryFormModalProps) {
  const { createCategory, updateCategory, isCreating, isUpdating } = useProductCategories();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: initialName || '',
      });
    }
  }, [open, initialName, form]);

  const onSubmit = async (data: FormData) => {
    try {
      if (categoryId) {
        await updateCategory({ id: categoryId, name: data.name });
      } else {
        await createCategory({ name: data.name });
      }
      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <FormModal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={categoryId ? 'Editar Categoría' : 'Nueva Categoría'}
      form={form}
      onSubmit={onSubmit}
      submitLabel={categoryId ? 'Guardar' : 'Crear'}
      isLoading={isLoading}
      maxWidth="max-w-[400px]"
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nombre</FormLabel>
            <FormControl>
              <Input placeholder="Ej: Limpieza, Decoración..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FormModal>
  );
}
